import { readProjectSettings } from '../admin/adminSettings';
import { readAdminSession } from '../admin/authProvider';

export type YouTubeContent = {
    id: string;
    youtubeContentId: string;
    title: string;
    youtubeUrl: string;
    displayOrder: number;
    isActive: boolean;
    createdAt: string;
    updatedAt: string;
};

export type YouTubeContentInput = Pick<YouTubeContent, 'title' | 'youtubeUrl' | 'displayOrder' | 'isActive'>;

export type QuickReadAloudPrompt = {
    promptId: string;
    paragraph: string;
    examName: 'Academic' | 'Core';
    isActive: boolean;
    createdAt: string;
    updatedAt: string;
};

export type QuickReadAloudPromptInput = Pick<QuickReadAloudPrompt, 'paragraph' | 'examName' | 'isActive'>;

export type HomePageImage = {
    id: string;
    fileName: string;
    fileKey: string;
    imageUrl: string;
    order: number;
};

export type HomePageContentPromotionChangeCount = {
    insert: number;
    update: number;
    deactivate: number;
    remove: number;
};

export type HomePageContentPromotionResult = {
    sourceEnvironment: string;
    targetEnvironment: string;
    canPublish: boolean;
    published: boolean;
    publishedAtUtc: string | null;
    blockers: string[];
    warnings: string[];
    changes: Record<string, HomePageContentPromotionChangeCount>;
    imageFilesToCopy: number;
    imageFilesToRemove: number;
    imageFilesCopied: number;
    imageFilesRemoved: number;
};

type HomePageImagesResponse = {
    baseUrl: string;
    images: HomePageImage[];
};

type YouTubeContentResponse = Partial<YouTubeContent> & {
    youTubeContentId?: string;
    youTubeUrl?: string;
};

type Envelope<T> = { success?: boolean; data?: T; errorMessage?: string | null; message?: string };

function apiConfig() {
    const settings = readProjectSettings();
    const session = readAdminSession();
    const base = String(import.meta.env.VITE_API_BASE_URL ?? import.meta.env.VITE_API_URL ?? settings.adminApiBaseUrl ?? 'https://test-api.pteultra.com/api/v1').replace(/\/+$/, '');
    const root = /\/api\/v[^/]+$/i.test(base) ? base : `${base}/api/v${String(import.meta.env.VITE_API_VERSION ?? settings.adminApiVersion ?? '1')}`;
    const token = String(session?.adminToken ?? session?.accessToken ?? import.meta.env.VITE_ADMIN_TOKEN ?? settings.adminApiToken ?? '');
    return { root, token };
}

function unwrap<T>(payload: T | Envelope<T>): T {
    if (payload && typeof payload === 'object' && 'success' in payload) {
        const envelope = payload as Envelope<T>;
        if (envelope.success === false) throw new Error(envelope.errorMessage ?? envelope.message ?? 'Request failed');
        return envelope.data as T;
    }
    return payload as T;
}

function normalize(value: YouTubeContentResponse): YouTubeContent {
    const id = value.youTubeContentId ?? value.youtubeContentId ?? value.id ?? '';
    return {
        id,
        youtubeContentId: id,
        title: value.title ?? '',
        youtubeUrl: value.youTubeUrl ?? value.youtubeUrl ?? '',
        displayOrder: Number(value.displayOrder) || 0,
        isActive: value.isActive !== false,
        createdAt: value.createdAt ?? '',
        updatedAt: value.updatedAt ?? '',
    };
}

async function request<T>(path = '', init: RequestInit = {}): Promise<T> {
    const { root, token } = apiConfig();
    const headers = new Headers(init.headers);
    headers.set('Accept', 'application/json');
    if (init.body) headers.set('Content-Type', 'application/json');
    if (token) headers.set('Authorization', `Bearer ${token}`);
    const response = await fetch(`${root}/AdminHomePageContents/youtube${path}`, { ...init, headers });
    const payload = await response.json().catch(() => null) as T | Envelope<T> | null;
    if (!response.ok) throw new Error((payload as Envelope<T> | null)?.errorMessage ?? `${response.status} ${response.statusText}`);
    return unwrap(payload as T | Envelope<T>);
}

async function quickReadAloudRequest<T>(path = '', init: RequestInit = {}): Promise<T> {
    const { root, token } = apiConfig();
    const headers = new Headers(init.headers);
    headers.set('Accept', 'application/json');
    if (init.body) headers.set('Content-Type', 'application/json');
    if (token) headers.set('Authorization', `Bearer ${token}`);
    const response = await fetch(`${root}/AdminQuickReadAloud/prompts${path}`, { ...init, headers });
    const payload = await response.json().catch(() => null) as T | Envelope<T> | null;
    if (!response.ok) throw new Error((payload as Envelope<T> | null)?.errorMessage ?? `${response.status} ${response.statusText}`);
    return unwrap(payload as T | Envelope<T>);
}

async function homePageImageRequest<T>(collection: 'score-reports' | 'carousel-images', path = '', init: RequestInit = {}): Promise<T> {
    const { root, token } = apiConfig();
    const headers = new Headers(init.headers);
    headers.set('Accept', 'application/json');
    if (token) headers.set('Authorization', `Bearer ${token}`);
    const response = await fetch(`${root}/AdminHomePageContents/${collection}${path}`, { ...init, headers });
    const payload = await response.json().catch(() => null) as T | Envelope<T> | null;
    if (!response.ok) throw new Error((payload as Envelope<T> | null)?.errorMessage ?? `${response.status} ${response.statusText}`);
    return unwrap(payload as T | Envelope<T>);
}

export async function getYouTubeContents(): Promise<YouTubeContent[]> {
    return (await request<YouTubeContentResponse[]>()).map(normalize);
}

export async function saveYouTubeContent(input: YouTubeContentInput, id?: string): Promise<YouTubeContent> {
    return normalize(await request<YouTubeContentResponse>(id ? `/${id}` : '', { method: id ? 'PUT' : 'POST', body: JSON.stringify(input) }));
}

export async function deleteYouTubeContent(id: string): Promise<void> {
    await request(`/${id}`, { method: 'DELETE' });
}

export async function getQuickReadAloudPrompts(): Promise<QuickReadAloudPrompt[]> {
    return quickReadAloudRequest<QuickReadAloudPrompt[]>();
}

export async function saveQuickReadAloudPrompt(input: QuickReadAloudPromptInput, promptId?: string): Promise<QuickReadAloudPrompt> {
    return quickReadAloudRequest<QuickReadAloudPrompt>(promptId ? `/${promptId}` : '', {
        method: promptId ? 'PUT' : 'POST',
        body: JSON.stringify(input),
    });
}

export async function getScoreReportImages(): Promise<HomePageImage[]> {
    return (await homePageImageRequest<HomePageImagesResponse>('score-reports')).images ?? [];
}

async function promotionRequest<T>(path: 'validate-publish' | 'publish-target'): Promise<T> {
    const { root, token } = apiConfig();
    const headers = new Headers({ Accept: 'application/json', 'Content-Type': 'application/json' });
    if (token) headers.set('Authorization', `Bearer ${token}`);
    const response = await fetch(`${root}/AdminHomePageContents/${path}`, { method: 'POST', headers });
    const payload = await response.json().catch(() => null) as T | Envelope<T> | null;
    if (!response.ok) throw new Error((payload as Envelope<T> | null)?.errorMessage ?? `${response.status} ${response.statusText}`);
    return unwrap(payload as T | Envelope<T>);
}

function normalizePromotionResult(value: Partial<HomePageContentPromotionResult>): HomePageContentPromotionResult {
    return {
        sourceEnvironment: String(value.sourceEnvironment ?? ''),
        targetEnvironment: String(value.targetEnvironment ?? ''),
        canPublish: value.canPublish === true,
        published: value.published === true,
        publishedAtUtc: value.publishedAtUtc ?? null,
        blockers: Array.isArray(value.blockers) ? value.blockers.map(String) : [],
        warnings: Array.isArray(value.warnings) ? value.warnings.map(String) : [],
        changes: value.changes ?? {},
        imageFilesToCopy: Number(value.imageFilesToCopy ?? 0),
        imageFilesToRemove: Number(value.imageFilesToRemove ?? 0),
        imageFilesCopied: Number(value.imageFilesCopied ?? 0),
        imageFilesRemoved: Number(value.imageFilesRemoved ?? 0),
    };
}

export async function uploadScoreReportImage(file: File, id?: string): Promise<HomePageImage> {
    const body = new FormData();
    body.append('file', file);
    return homePageImageRequest<HomePageImage>('score-reports', id ? `/${id}` : '', { method: id ? 'PUT' : 'POST', body });
}

export async function deleteScoreReportImage(id: string): Promise<void> {
    await homePageImageRequest('score-reports', `/${id}`, { method: 'DELETE' });
}

export async function reorderScoreReportImages(imageIds: string[]): Promise<HomePageImage[]> {
    return (await homePageImageRequest<HomePageImagesResponse>('score-reports', '/order', {
        method: 'PUT', body: JSON.stringify({ imageIds }), headers: { 'Content-Type': 'application/json' },
    })).images ?? [];
}

export async function getCarouselImages(): Promise<HomePageImage[]> {
    return (await homePageImageRequest<HomePageImagesResponse>('carousel-images')).images ?? [];
}

export async function uploadCarouselImage(file: File, id?: string): Promise<HomePageImage> {
    const body = new FormData();
    body.append('file', file);
    return homePageImageRequest<HomePageImage>('carousel-images', id ? `/${id}` : '', { method: id ? 'PUT' : 'POST', body });
}

export async function deleteCarouselImage(id: string): Promise<void> {
    await homePageImageRequest('carousel-images', `/${id}`, { method: 'DELETE' });
}

export async function reorderCarouselImages(imageIds: string[]): Promise<HomePageImage[]> {
    return (await homePageImageRequest<HomePageImagesResponse>('carousel-images', '/order', {
        method: 'PUT', body: JSON.stringify({ imageIds }), headers: { 'Content-Type': 'application/json' },
    })).images ?? [];
}

export async function validateHomePageContentPublish(): Promise<HomePageContentPromotionResult> {
    return normalizePromotionResult(await promotionRequest<HomePageContentPromotionResult>('validate-publish'));
}

export async function publishHomePageContentToTarget(): Promise<HomePageContentPromotionResult> {
    return normalizePromotionResult(await promotionRequest<HomePageContentPromotionResult>('publish-target'));
}
